import { Inject, Injectable } from '@angular/core';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable } from 'rxjs';
import { WhitelabelNameSubstitutions } from './whitelabel-translation.pipe';
import { WhitelabelService } from '@galaxy/partner';
import { PARTNER_ID, MARKET_ID } from '../tokens';
import { ConfigurationInterface } from '@vendasta/partner';

@Injectable({
  providedIn: 'root',
})
export class WhitelabelTranslationService {
  private instantParams: WhitelabelNameSubstitutions;
  private readonly cachedPartnerId$: Observable<string>;
  private readonly whitelabelConfiguration$: Observable<ConfigurationInterface>;

  constructor(
    private readonly translate: TranslateService,
    @Inject(PARTNER_ID) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID) private readonly marketId$: Observable<string>,
    private readonly whitelabelService: WhitelabelService,
  ) {
    this.cachedPartnerId$ = this.partnerId$.pipe(shareReplay(1));
    this.whitelabelConfiguration$ = combineLatest([this.cachedPartnerId$, this.marketId$]).pipe(
      switchMap(([partnerId, marketId]) => this.whitelabelService.getConfiguration(partnerId, marketId)),
      shareReplay(1),
    );
    this.getWhitelabelledParams$({}).subscribe((defaultParams) => {
      this.instantParams = defaultParams;
    });
  }

  stream(translationString: string | string[], interpolateParams?: object): Observable<any> {
    return this.getWhitelabelledParams$(interpolateParams).pipe(
      switchMap((params) => this.translate.stream(translationString, params)),
    );
  }

  instant(translationParams: string | string[], interpolateParams?: object): any {
    let translateParams;
    if (interpolateParams) {
      translateParams = Object.assign(interpolateParams, this.instantParams);
    }

    return this.translate.instant(translationParams, translateParams || this.instantParams);
  }

  private getWhitelabelledParams$(interpolateParams: object): Observable<WhitelabelNameSubstitutions> {
    return this.whitelabelConfiguration$.pipe(
      map((config) => config?.snapshotConfiguration?.snapshotReportName || 'Snapshot Report'),
      map((snapshotName) => {
        let translateParams = <WhitelabelNameSubstitutions>{
          appName: 'Sales & Success Center',
          snapshotName: snapshotName,
        };

        if (interpolateParams) {
          translateParams = Object.assign(interpolateParams, translateParams);
        }

        return translateParams;
      }),
    );
  }
}
